import { CouponAction, CouponType, WelfareCouponItem } from './types';

export const formatWelfareAmount = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return '--';
  return String(value);
};

/** 累计交易量统一以 K（千）展示，不切换为 M/B。 */
export const formatVolumeLabel = (raw: string | number) => {
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(raw);
  return `${trimZero(num / 1e3)}K`;
};

const trimZero = (n: number) => {
  const fixed = n.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
};

/** 解析进度字段：接口可能返回纯数字或带币种后缀，如 "100" / "100USDT" */
export const parseProgressNumber = (raw?: string | number | null) => {
  if (raw === undefined || raw === null || raw === '') return NaN;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  const matched = String(raw).replace(/,/g, '').match(/-?\d+(\.\d+)?/);
  return matched ? Number(matched[0]) : NaN;
};

export const progressPercent = (current?: string | number, target?: string | number) => {
  const c = parseProgressNumber(current);
  const t = parseProgressNumber(target);
  if (!Number.isFinite(c) || !Number.isFinite(t) || t <= 0) return 0;
  return Math.min(100, Math.max(0, (c / t) * 100));
};

/**
 * 累计档位轨道填充比例（0~100）。
 * 档位等距排布，轨道从首档节点画到末档节点；
 * 未达首档时为 0；已达档位后，段内进度：跨度 ≥10 倍时用对数插值，否则线性。
 */
export const cumulativeTrackFillPercent = (
  tiers: Array<{ targetValue?: string | number; rewardStatus?: string }>,
  progressCurrent?: string | number | null,
) => {
  if (!tiers.length) return 0;
  if (tiers.length === 1) {
    const target = parseProgressNumber(tiers[0].targetValue);
    const current = parseProgressNumber(progressCurrent);
    if (!Number.isFinite(target) || target <= 0 || !Number.isFinite(current)) return 0;
    return current >= target || tiers[0].rewardStatus === 'ISSUED' ? 100 : Math.min(100, Math.max(0, (current / target) * 100));
  }

  const sorted = [...tiers].sort(
    (a, b) => parseProgressNumber(a.targetValue) - parseProgressNumber(b.targetValue),
  );
  const current = parseProgressNumber(progressCurrent);
  const safeCurrent = Number.isFinite(current) ? current : 0;

  const lastReached = sorted.reduce((idx, tier, i) => {
    const target = parseProgressNumber(tier.targetValue);
    if (tier.rewardStatus === 'ISSUED' || (Number.isFinite(target) && safeCurrent >= target)) {
      return i;
    }
    return idx;
  }, -1);

  if (lastReached >= sorted.length - 1) return 100;

  const segmentRatio = (from: number, to: number, value: number) => {
    if (!(to > from)) return value >= to ? 1 : 0;
    if (value <= from) return 0;
    if (value >= to) return 1;
    // 数量级跨度大时线性几乎不可见，用对数映射段内进度
    if (from > 0 && to / from >= 10) {
      return Math.log(value / from) / Math.log(to / from);
    }
    return (value - from) / (to - from);
  };

  if (lastReached < 0) {
    // 首个节点即第一档门槛：未达标前轨道从 0 起画会看起来像已越过首档，故保持 0。
    return 0;
  }

  const from = parseProgressNumber(sorted[lastReached].targetValue);
  const to = parseProgressNumber(sorted[lastReached + 1].targetValue);
  if (!Number.isFinite(from) || !Number.isFinite(to)) {
    return (lastReached / (sorted.length - 1)) * 100;
  }
  const extra = segmentRatio(from, to, safeCurrent);
  return ((lastReached + extra) / (sorted.length - 1)) * 100;
};

/** 进度文案：拆成数量与单位，便于数量加粗、单位不加粗 */
export const formatProgressParts = (
  current?: string | number | null,
  target?: string | number | null,
  coin?: string,
) => {
  const currentText = current == null || current === '' ? '0' : String(current);
  const rawTarget = target == null || target === '' ? '0' : String(target);
  const coinText = coin ? String(coin) : '';
  const coinPattern = coinText ? new RegExp(`\\s*${coinText}\\s*$`, 'i') : null;
  const targetHasCoin = coinPattern ? coinPattern.test(rawTarget) : /[A-Za-z]/.test(rawTarget);
  const targetText = coinPattern ? rawTarget.replace(coinPattern, '') : rawTarget;
  const unit = targetHasCoin ? coinText || rawTarget.replace(/^[\d.,\s\/]+/, '') : coinText;
  return {
    pair: `${currentText}/${targetText}`,
    unit: unit ? ` ${unit}` : '',
  };
};

/** 进度文案：目标已含币种时不再追加 coin，避免 "100USDT USDT" */
export const formatProgressPair = (
  current?: string | number | null,
  target?: string | number | null,
  coin?: string,
) => {
  const { pair, unit } = formatProgressParts(current, target, coin);
  return `${pair}${unit}`;
};

export const getCouponActions = (coupon: WelfareCouponItem): CouponAction[] => {
  if (Array.isArray(coupon.allowedActions) && coupon.allowedActions.length) {
    return coupon.allowedActions;
  }
  if (coupon.buttonCode) return [coupon.buttonCode];
  return [];
};

/** RECEIVE_SUCCESS 与 USED 展示一致，且优先于 USE，避免已使用列表仍显示「去使用」 */
export const isCouponUsedAction = (coupon: WelfareCouponItem) => {
  const status = String(coupon.status || '').toUpperCase();
  const buttonCode = String(coupon.buttonCode || '').toUpperCase();
  if (status === 'RECEIVE_SUCCESS' || status === 'USED' || status === 'USING') return true;
  if (buttonCode === 'RECEIVE_SUCCESS' || buttonCode === 'USED') return true;
  const actions = getCouponActions(coupon);
  return actions.includes('USED') || actions.includes('RECEIVE_SUCCESS');
};

export const couponTypeI18nKey = (type?: CouponType | 'ALL' | string) => {
  if (!type || type === 'ALL') return 'welfareCenter.couponType.ALL';
  // 接口可能返回 TASK_FEE_DEDUCT / NORMAL_FEE_DEDUCT，筛选文案用家族名
  const normalized = String(type).replace(/^(TASK_|NORMAL_)/, '') as CouponType;
  return `welfareCenter.couponType.${normalized}`;
};

/** 列表/详情标题：优先按类型走 i18n，避免接口中文名覆盖多语言 */
export const getCouponTitle = (
  coupon?: {
    couponFamily?: string;
    couponFamilyName?: string;
    couponName?: string;
    couponTypeDesc?: string;
    couponFamilyDesc?: string;
    couponType?: string;
  } | null,
  t?: (key: string, options?: { defaultValue?: string }) => string,
) => {
  if (!coupon) return '--';
  const typeKey = coupon.couponFamily || coupon.couponType;
  if (t && typeKey) {
    const i18nKey = couponTypeI18nKey(typeKey);
    const translated = t(i18nKey, { defaultValue: '' });
    // 有翻译且不是原样回退 key 时使用
    if (translated && translated !== i18nKey) {
      return translated;
    }
  }
  if (coupon.couponFamilyName) return coupon.couponFamilyName;
  if (coupon.couponFamilyDesc) return coupon.couponFamilyDesc;
  if (coupon.couponTypeDesc) return coupon.couponTypeDesc;
  if (coupon.couponName) return coupon.couponName;
  return coupon.couponType || '--';
};

/** 接口可能直接返回中文「系统发放」或枚举 SYSTEM，统一映射到 i18n */
const REWARD_SOURCE_ALIASES: Record<string, string> = {
  SYSTEM: 'system',
  SYSTEM_GRANT: 'system',
  SYSTEM_ISSUE: 'system',
  '系统发放': 'system',
  '系統發放': 'system',
  'System grant': 'system',
  'System Grant': 'system',
};

export const getRewardSourceLabel = (
  source?: string | null,
  t?: (key: string, options?: { defaultValue?: string }) => string,
) => {
  if (source == null || source === '') return '--';
  const raw = String(source).trim();
  if (!t) return raw;

  const enumCode = raw.toUpperCase().replace(/[\s-]+/g, '_');
  const alias =
    REWARD_SOURCE_ALIASES[raw] ||
    REWARD_SOURCE_ALIASES[enumCode] ||
    (/^[A-Z][A-Z0-9_]*$/.test(enumCode) ? enumCode.toLowerCase() : '');

  if (alias) {
    const i18nKey = `welfareCenter.rewardHistory.source.${alias}`;
    const translated = t(i18nKey, { defaultValue: '' });
    if (translated && translated !== i18nKey) return translated;
  }
  return raw;
};

export const isPositionExperienceCoupon = (coupon?: {
  couponType?: string;
  couponFamily?: string;
} | null) => {
  if (!coupon) return false;
  if (coupon.couponFamily === 'POSITION_EXPERIENCE') return true;
  return String(coupon.couponType || '').includes('POSITION_EXPERIENCE');
};

export const isVipCoupon = (coupon?: {
  couponType?: string;
  couponFamily?: string;
} | null) => {
  if (!coupon) return false;
  if (coupon.couponFamily === 'VIP') return true;
  return String(coupon.couponType || '').replace(/^(TASK_|NORMAL_)/, '') === 'VIP';
};

export const formatCouponSymbols = (
  symbols?: Array<string | { symbol?: string }> | null,
) => {
  if (!Array.isArray(symbols) || !symbols.length) return '--';
  const text = symbols
    .map(item => (typeof item === 'string' ? item : item?.symbol || ''))
    .filter(Boolean)
    .join('、');
  return text || '--';
};

/** 仓位有效期展示（小时）：与合约体验券一致，有 hours 用 hours，否则 days * 24 */
export const getPositionValidLabel = (coupon?: {
  positionValidDays?: number | null;
  positionValidHours?: number | null;
} | null) => {
  if (coupon?.positionValidHours != null) {
    return Number(coupon.positionValidHours);
  }
  if (coupon?.positionValidDays != null) {
    return Number(coupon.positionValidDays) * 24;
  }
  return '--';
};

/** BTCUSDT / btc_usdt / BTC → 合约路由 symbol identity */
export const toFuturesSymbolIdentity = (raw?: string | null) => {
  if (!raw) return 'btc_usdt';
  const text = String(raw).trim();
  if (!text) return 'btc_usdt';
  if (text.includes('_')) return text.toLowerCase();
  const upper = text.toUpperCase();
  if (upper.endsWith('USDT')) {
    return `${upper.slice(0, -4).toLowerCase()}_usdt`;
  }
  // 仅币种：BTC → btc_usdt
  return `${upper.toLowerCase()}_usdt`;
};

const pickFirstCouponSymbol = (
  symbols?: Array<string | { symbol?: string }> | null,
) => {
  if (!Array.isArray(symbols) || !symbols.length) return '';
  const first = symbols[0];
  return typeof first === 'string' ? first : first?.symbol || '';
};

/** 仓位体验券：跳合约交易页体验券模式，并选中指定券 */
export const buildPositionExperienceTradePath = (opts: {
  userCouponId: number | string;
  symbol?: string | null;
  symbols?: Array<string | { symbol?: string }> | null;
}) => {
  const identity = toFuturesSymbolIdentity(opts.symbol || pickFirstCouponSymbol(opts.symbols) || 'BTCUSDT');
  return `/futures/contract/u_based/${identity}?experience=1&userCouponId=${opts.userCouponId}`;
};

export const getTierCouponAmount = (tier?: { couponAmount?: string | number; amount?: string | number }) =>
  Number(tier?.couponAmount ?? tier?.amount ?? 0);

export const sumRewardTiersAmount = (
  tiers?: Array<{ couponAmount?: string | number; amount?: string | number }>,
  fallback?: string | number,
) => {
  if (Array.isArray(tiers) && tiers.length) {
    const total = tiers.reduce((sum, tier) => sum + getTierCouponAmount(tier), 0);
    // 档位金额缺失时回退任务级 couponAmount，避免误显 0
    return total > 0 ? total : Number(fallback || 0);
  }
  return Number(fallback || 0);
};
